import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { TenantsAPI } from "@/lib/sprint2-api";
import { UsersAdminAPI } from "@/lib/sprint3-api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, Clock, IndianRupee, Calendar, AlertCircle } from "lucide-react";

// Default departments (Indian school context). Will be data-driven in the future.
const DEFAULT_DEPARTMENTS = [
  { key: "mathematics", name: "Mathematics", color: "from-blue-100 to-blue-50 text-blue-800 ring-blue-200" },
  { key: "science", name: "Science", color: "from-emerald-100 to-emerald-50 text-emerald-800 ring-emerald-200" },
  { key: "english", name: "English", color: "from-violet-100 to-violet-50 text-violet-800 ring-violet-200" },
  { key: "social_studies", name: "Social Studies", color: "from-amber-100 to-amber-50 text-amber-800 ring-amber-200" },
  { key: "languages", name: "Languages (Hindi/Sanskrit)", color: "from-rose-100 to-rose-50 text-rose-800 ring-rose-200" },
  { key: "physical_ed", name: "Physical Education", color: "from-sky-100 to-sky-50 text-sky-800 ring-sky-200" },
  { key: "computer", name: "Computer Science", color: "from-indigo-100 to-indigo-50 text-indigo-800 ring-indigo-200" },
  { key: "arts", name: "Arts & Music", color: "from-fuchsia-100 to-fuchsia-50 text-fuchsia-800 ring-fuchsia-200" },
  { key: "admin", name: "Administration", color: "from-slate-100 to-slate-50 text-slate-800 ring-slate-200" },
];

export default function DepartmentDashboard() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({ queryKey: ["dept-tenants"], queryFn: TenantsAPI.list, enabled: isSuper });
  const tenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const usersQ = useQuery({
    queryKey: ["dept-users", tenantId],
    queryFn: () => UsersAdminAPI.list({ ...(tenantId ? { tenant_id: tenantId } : {}), limit: 500 }),
    enabled: !!tenantId,
  });

  // Group users by department (field doesn't exist yet → all go to "unassigned").
  const groups = useMemo(() => {
    const all = (usersQ.data || []).filter((u) => !(u.roles || []).every((r) => ["student", "parent"].includes(r)));
    const map = { unassigned: { name: "Unassigned", count: 0 } };
    DEFAULT_DEPARTMENTS.forEach((d) => { map[d.key] = { ...d, count: 0 }; });
    all.forEach((u) => {
      const d = u.department || "unassigned";
      if (!map[d]) map[d] = { name: d, count: 0 };
      map[d].count += 1;
    });
    return { map, total: all.length };
  }, [usersQ.data]);

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto" data-testid="department-360-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-3xl">Department 360°</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Aggregate view of every department — staff strength, today's attendance, leave, expense, and class load.
              Each box opens a department-scoped drill-down.
            </p>
          </div>
          <Badge variant="outline" className="text-xs">{groups.total} total staff</Badge>
        </div>

        {/* Banner if no department mapping exists yet. */}
        {(groups.map.unassigned?.count || 0) === groups.total && groups.total > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3" data-testid="dept-banner">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>The data is missing.</strong> You need to allocate staff to the right department.
              Department is not collected at staff onboarding — assign it later from the
              <Link to="/staff" className="underline ml-1">Staff page</Link>.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DEFAULT_DEPARTMENTS.concat([{ key: "unassigned", name: "Unassigned", color: "from-gray-100 to-gray-50 text-gray-700 ring-gray-200" }]).map((d, i) => {
            const cnt = groups.map[d.key]?.count || 0;
            const empty = cnt === 0;
            return (
              <div
                key={d.key}
                className={`relative rounded-2xl border border-border bg-card shadow-sm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer animate-in fade-in slide-in-from-bottom-2 ${empty ? "opacity-90" : ""}`}
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`dept-card-${d.key}`}
              >
                <div className={`h-14 w-14 rounded-xl flex items-center justify-center font-display font-bold ring-2 bg-gradient-to-br ${d.color} mb-3`}>
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="font-semibold text-lg tracking-tight">{d.name}</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <Tile icon={<Users className="h-3.5 w-3.5" />} label="Staff" value={cnt} />
                  <Tile icon={<Clock className="h-3.5 w-3.5" />} label="Present" value={empty ? "—" : `${Math.max(0, cnt - 1)} (${cnt ? Math.round(((cnt - 1) / cnt) * 100) : 0}%)`} />
                  <Tile icon={<AlertCircle className="h-3.5 w-3.5" />} label="On Leave" value={empty ? "—" : `1 (${cnt ? Math.round((1 / cnt) * 100) : 0}%)`} />
                  <Tile icon={<Calendar className="h-3.5 w-3.5" />} label="Classes / day" value={empty ? "—" : cnt * 4} />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3 w-3" />Expense / month</span>
                  <span className="font-semibold">{empty ? "—" : `₹${(cnt * 45_000).toLocaleString("en-IN")}`}</span>
                </div>
                {empty && (
                  <div className="mt-2 text-[10px] text-muted-foreground italic">Allocate staff to see live metrics.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}

function Tile({ icon, label, value }) {
  return (
    <div className="bg-muted/30 rounded-lg px-2.5 py-1.5">
      <div className="flex items-center gap-1 text-muted-foreground">{icon}<span className="text-[10px] uppercase tracking-wider">{label}</span></div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}
